import { Controller } from "@hotwired/stimulus"

/**
 * Bulk Actions Controller
 *
 * Adds select-all / deselect-all with a floating action bar for tables.
 *
 * Usage:
 *   <div data-controller="bulk-actions">
 *     <table>
 *       <thead>
 *         <tr>
 *           <th><input type="checkbox" data-action="change->bulk-actions#toggleAll" data-bulk-actions-target="selectAll"></th>
 *           <th>Name</th>
 *         </tr>
 *       </thead>
 *       <tbody>
 *         <tr>
 *           <td><input type="checkbox" data-action="change->bulk-actions#toggle" data-bulk-actions-target="checkbox" value="1"></td>
 *           <td>Alice</td>
 *         </tr>
 *       </tbody>
 *     </table>
 *
 *     <!-- Action bar (hidden until items selected) -->
 *     <div data-bulk-actions-target="bar" class="hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
 *       <div class="bulk-actions-bar">
 *         <span class="bulk-actions-count" data-bulk-actions-target="count">0</span>
 *         <span>selected</span>
 *         <div class="bulk-actions-separator"></div>
 *         <button class="bulk-actions-btn" data-action="bulk-actions#exportSelected">Export</button>
 *         <button class="bulk-actions-btn bulk-actions-btn--danger" data-action="bulk-actions#deleteSelected">Delete</button>
 *       </div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "bar", "count"]

  connect() {
    this.updateUI()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach(cb => {
      cb.checked = checked
    })
    this.updateUI()
  }

  toggle() {
    this.updateUI()
  }

  updateUI() {
    const selected = this.selectedValues
    const total = this.checkboxTargets.length

    // Update select-all checkbox state
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = total > 0 && selected.length === total
      this.selectAllTarget.indeterminate = selected.length > 0 && selected.length < total
    }

    // Show/hide action bar
    if (this.hasBarTarget) {
      if (selected.length > 0) {
        this.barTarget.classList.remove("hidden")
      } else {
        this.barTarget.classList.add("hidden")
      }
    }

    // Update count
    if (this.hasCountTarget) {
      this.countTarget.textContent = selected.length
    }
  }

  get selectedValues() {
    return this.checkboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)
  }

  // Override these in your view or extend the controller
  exportSelected(event) {
    event.preventDefault()
    const ids = this.selectedValues
    // Dispatch custom event for the page to handle
    this.dispatch("export", { detail: { ids } })
  }

  deleteSelected(event) {
    event.preventDefault()
    const ids = this.selectedValues
    // Dispatch custom event for the page to handle
    this.dispatch("delete", { detail: { ids } })
  }

  // Deselect all
  clearSelection() {
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasSelectAllTarget) this.selectAllTarget.checked = false
    this.updateUI()
  }
}
